import type { SymbolComponent } from '@tsagko/label-contracts'
import { resolveValueSource } from '../internal/units'

/**
 * EAN-13 helpers (sub-task 2a.10.7).
 *
 * EAN-13 (and its GS1-12 GTIN-13 variant) uses a mod-10 check digit appended
 * as the 13th digit. Given 12 digits `d1..d12` indexed left to right:
 *
 *   sum = d1×1 + d2×3 + d3×1 + d4×3 + ... + d11×1 + d12×3
 *   check = (10 - (sum mod 10)) mod 10
 *
 * Equivalently, from the rightmost of the 12 digits, weights alternate
 * 3, 1, 3, 1, ... This is the canonical GS1 mod-10 algorithm used by GTIN-12
 * (UPC-A), GTIN-13 (EAN-13), and GTIN-14 (ITF-14) — only the payload length
 * differs.
 *
 * These helpers are pure and stateless. `computeEan13CheckDigit` is used
 * internally by `validateEan13` (which `lintTemplate` calls); the deriver no
 * longer appends a check digit — the printer/renderer computes the 13th digit
 * from the ≤12 data digits we emit. `validateEan13` is exposed for
 * `lintTemplate` (sub-task 2a.10.15) — it does NOT throw from the runtime
 * path, which stays permissive on bad input (silent skip → lint warning).
 *
 * `derivePayloadForEan13` was relocated here in sub-task 2a.10.10 from
 * `internal/compile-symbol.ts` so the four symbology payload helpers
 * (`code-128`, `ean-13`, `qr`, `gs1-128`) all live in `barcodes/` with
 * symmetrical structure.
 */

const DIGITS_RE = /^\d{12}$/

/**
 * Compute the GS1 mod-10 check digit for a 12-digit EAN-13/GTIN-13 payload.
 *
 * Throws on inputs that are not exactly 12 ASCII digits. Callers that don't
 * have a strict 12-digit string in hand should branch on length BEFORE
 * calling this — `validateEan13` does exactly that.
 */
export function computeEan13CheckDigit(twelveDigits: string): string {
  if (!DIGITS_RE.test(twelveDigits)) {
    throw new Error(
      `computeEan13CheckDigit: expected exactly 12 ASCII digits, got ${JSON.stringify(twelveDigits)}`
    )
  }
  let sum = 0
  for (let i = 0; i < 12; i++) {
    // Position 1 is leftmost (i=0), position 12 is rightmost (i=11).
    // Even-indexed positions (d2, d4, ..., d12) — i=1,3,5,7,9,11 — are ×3.
    // Odd-indexed positions (d1, d3, ..., d11) — i=0,2,4,6,8,10 — are ×1.
    const digit = twelveDigits.charCodeAt(i) - 48
    const weight = i % 2 === 0 ? 1 : 3
    sum += digit * weight
  }
  const checkDigit = (10 - (sum % 10)) % 10
  return String(checkDigit)
}

export type Ean13ValidationResult = { ok: true } | { ok: false; reason: string }

/**
 * Validate that `value` is a well-formed 13-digit EAN-13: 13 ASCII digits AND
 * the trailing digit matches the GS1 mod-10 check digit computed over the
 * first 12. Returns a discriminated result rather than throwing so callers
 * (lintTemplate in sub-task 2a.10.15) can present the failure as a warning.
 */
export function validateEan13(value: string): Ean13ValidationResult {
  if (value.length !== 13) {
    return {
      ok: false,
      reason: `EAN-13 must be exactly 13 characters; got length ${value.length}`,
    }
  }
  if (!/^\d{13}$/.test(value)) {
    return { ok: false, reason: 'EAN-13 must contain only ASCII digits 0-9' }
  }
  const expected = computeEan13CheckDigit(value.slice(0, 12))
  const actual = value.charAt(12)
  if (expected !== actual) {
    return {
      ok: false,
      reason: `EAN-13 check digit mismatch: expected ${expected}, got ${actual}`,
    }
  }
  return { ok: true }
}

/**
 * Build the EAN-13 payload string from a `SymbolComponent`.
 *
 *   - `gtin`   → resolve the ValueSource, strip non-digits, emit at most 12
 *                digits.
 *   - `raw`    → resolve, strip non-digits, emit at most 12 digits (identical
 *                to `gtin` here; length mismatches surface as lint warnings).
 *   - `gs1-ai` → `undefined` (silent skip — semantically nonsensical for
 *                EAN-13; GS1-128 is the right symbology).
 *   - `url`    → `undefined` (silent skip — URLs are not EAN-13 payloads).
 *
 * Returns `undefined` to signal "skip this primitive" to the caller.
 *
 * The printer/renderer (`^BE`) owns and computes the 13th check digit, so we
 * never transmit it: any digit beyond the 12th (including a supplied check
 * digit) is dropped here. The `checksum` field on the `gtin` structure is
 * intentionally NOT read in this deriver — `lintTemplate` (`lint-template.ts`)
 * reads it separately to emit the validated-mode warning.
 *
 * Relocated from `internal/compile-symbol.ts` in sub-task 2a.10.10 so all
 * four symbology payload derivers (`code-128`, `ean-13`, `qr`, `gs1-128`)
 * live side-by-side under `barcodes/`.
 */
export function derivePayloadForEan13(
  component: SymbolComponent,
  data: Record<string, unknown>
): string | undefined {
  const structure = component.dataStructure
  switch (structure.kind) {
    case 'gtin':
    case 'raw': {
      const resolved = resolveValueSource(structure.value, data)
      const digits = resolved.replace(/\D/g, '')
      // The printer/renderer owns the EAN-13 check digit: send ≤12 data digits
      // and `^BE` computes the 13th. A supplied 13th (or any extra) is dropped
      // here so we never transmit a check digit the printer would recompute.
      // Length/validity is surfaced by lintTemplate, not enforced here.
      return digits.length > 12 ? digits.slice(0, 12) : digits
    }
    case 'gs1-ai':
    case 'url':
      // Nonsensical for EAN-13 — silent skip. Lint flags the mismatch.
      return undefined
    default: {
      const _exhaustive: never = structure
      throw new Error(
        `derivePayloadForEan13: unknown structure ${JSON.stringify(_exhaustive)}`
      )
    }
  }
}
